/** Crop region in normalized frame coordinates (0..1). */
export interface CropRect {
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface FrameProcessing {
  mirror: boolean;
  crop: CropRect | null;
}

const clamp01 = (v: number) => Math.min(1, Math.max(0, v));

/** Rectangle from a drag in any direction, clamped to the frame. */
export function normalizeRect(a: { x: number; y: number }, b: { x: number; y: number }): CropRect {
  const x1 = clamp01(Math.min(a.x, b.x));
  const y1 = clamp01(Math.min(a.y, b.y));
  const x2 = clamp01(Math.max(a.x, b.x));
  const y2 = clamp01(Math.max(a.y, b.y));
  return { x: x1, y: y1, w: x2 - x1, h: y2 - y1 };
}

type Ctx = Pick<CanvasRenderingContext2D, 'save' | 'restore' | 'translate' | 'scale' | 'drawImage'>;

/**
 * Draw one frame of the shared call window: only the crop region (if any), flipped horizontally
 * when mirrored, so both tools see the client the right way round and nothing outside the tile.
 */
export function drawProcessedFrame(
  ctx: Ctx,
  canvas: { width: number; height: number },
  video: HTMLVideoElement,
  { mirror, crop }: FrameProcessing,
): void {
  const vw = video.videoWidth;
  const vh = video.videoHeight;
  if (!vw || !vh) return;
  const sx = Math.round((crop?.x ?? 0) * vw);
  const sy = Math.round((crop?.y ?? 0) * vh);
  const sw = Math.max(1, Math.round((crop?.w ?? 1) * vw));
  const sh = Math.max(1, Math.round((crop?.h ?? 1) * vh));
  if (canvas.width !== sw) canvas.width = sw;
  if (canvas.height !== sh) canvas.height = sh;
  ctx.save();
  if (mirror) {
    ctx.translate(sw, 0);
    ctx.scale(-1, 1);
  }
  ctx.drawImage(video, sx, sy, sw, sh, 0, 0, sw, sh);
  ctx.restore();
}

/** Portrait posture framing: width / height = 9 / 16 in source pixels. */
export const PORTRAIT_9_16 = 9 / 16;

/** Crop size in source-video pixels. */
export function cropPixelSize(rect: CropRect, videoWidth: number, videoHeight: number): { width: number; height: number } {
  return { width: Math.round(rect.w * videoWidth), height: Math.round(rect.h * videoHeight) };
}

/**
 * Rectangle from a drag, locked to `aspect` (width / height) in source pixels. By default it grows
 * with the larger drag component (`driver: 'both'`); edge resizes drive it from one axis only.
 * It keeps the drag direction from the start point and shrinks to stay inside the frame.
 */
export function constrainRectToAspect(
  start: { x: number; y: number },
  end: { x: number; y: number },
  videoWidth: number,
  videoHeight: number,
  aspect: number,
  driver: 'both' | 'x' | 'y' = 'both',
): CropRect {
  if (!videoWidth || !videoHeight) return normalizeRect(start, end);
  const sx = clamp01(start.x) * videoWidth;
  const sy = clamp01(start.y) * videoHeight;
  const dx = end.x * videoWidth - sx;
  const dy = end.y * videoHeight - sy;
  const dirX = dx < 0 ? -1 : 1;
  const dirY = dy < 0 ? -1 : 1;

  let h = driver === 'x' ? Math.abs(dx) / aspect : driver === 'y' ? Math.abs(dy) : Math.max(Math.abs(dy), Math.abs(dx) / aspect);
  let w = h * aspect;
  const roomW = dirX > 0 ? videoWidth - sx : sx;
  const roomH = dirY > 0 ? videoHeight - sy : sy;
  const scale = Math.min(1, w > 0 ? roomW / w : 1, h > 0 ? roomH / h : 1);
  w *= scale;
  h *= scale;

  const x = dirX > 0 ? sx : sx - w;
  const y = dirY > 0 ? sy : sy - h;
  return { x: x / videoWidth, y: y / videoHeight, w: w / videoWidth, h: h / videoHeight };
}
